import { useState } from 'react'
import { ErroRequisicao } from '../../api/cliente'
import { useResponderApoio, useSairDoApoio } from '../../api/hooks-chat'
import { Aviso, Botao, Modal } from '../../componentes/ui'

/**
 * As peças do apoio com pedido e devolução (ADR 0043), dentro da conversa e
 * no pop-up de quem foi chamado.
 */

/**
 * Aceitar ou recusar um pedido de apoio, com um comentário opcional.
 *
 * O mesmo nos dois lugares: no pop-up do canto da tela e na faixa da
 * conversa aberta pelo bloco "Solicitação de apoio". Botões no desenho
 * aprovado: verde com "Aceitar" em branco, vermelho com "Recusar".
 */
export function RespostaAoPedido({
  chamadoId,
  apoioId,
  aoResponder,
}: {
  chamadoId: string
  apoioId: string
  /** `aceitou` deixa quem chamou decidir o que vem depois: o cartão abre a conversa. */
  aoResponder?: (aceitou: boolean) => void
}) {
  const responder = useResponderApoio()
  const [comentario, setComentario] = useState('')
  const erro = responder.error instanceof ErroRequisicao ? responder.error : null

  function enviar(aceitar: boolean) {
    responder.mutate(
      { chamadoId, apoioId, aceitar, comentario: comentario.trim() || undefined },
      { onSuccess: () => aoResponder?.(aceitar) },
    )
  }

  return (
    <div className="space-y-2">
      <textarea
        value={comentario}
        onChange={(e) => setComentario(e.target.value)}
        placeholder="Comentário (opcional)"
        aria-label="Comentário da resposta ao pedido de apoio"
        rows={2}
        className="w-full resize-none rounded-md bg-superficie px-3 py-2 text-sm text-tinta-900 ring-1 ring-tinta-200 outline-none focus:ring-2 focus:ring-marca"
      />
      {erro ? <p className="text-xs text-red-700">{erro.corpo.mensagem}</p> : null}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={responder.isPending}
          onClick={() => enviar(true)}
          className="rounded-full bg-green-600 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-green-700 disabled:opacity-50"
        >
          Aceitar
        </button>
        <button
          type="button"
          disabled={responder.isPending}
          onClick={() => enviar(false)}
          className="rounded-full bg-red-600 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
        >
          Recusar
        </button>
      </div>
    </div>
  )
}

/** A faixa no alto da conversa de quem foi chamado e ainda não respondeu. */
export function FaixaDoPedido({
  chamadoId,
  pedido,
}: {
  chamadoId: string
  pedido: { apoioId: string; deNome: string; comentario: string | null }
}) {
  return (
    <div className="space-y-2 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p className="font-semibold">{pedido.deNome} pediu seu apoio nesta conversa</p>
      {pedido.comentario ? <p className="text-amber-800">"{pedido.comentario}"</p> : null}
      <div className="max-w-md">
        <RespostaAoPedido chamadoId={chamadoId} apoioId={pedido.apoioId} />
      </div>
    </div>
  )
}

/**
 * "Fechar para mim", com o recado opcional para o dono (questionário de
 * 05/10/2026). A conversa volta para o dono no bloco "Saiu do apoio".
 */
export function ModalFecharParaMim({
  chamadoId,
  donoNome,
  aberto,
  aoFechar,
}: {
  chamadoId: string
  donoNome: string | null
  aberto: boolean
  aoFechar: () => void
}) {
  const sair = useSairDoApoio()
  const [recado, setRecado] = useState('')
  const erro = sair.error instanceof ErroRequisicao ? sair.error : null
  const dono = donoNome ?? 'o responsável'

  return (
    <Modal
      aberto={aberto}
      titulo="Fechar para mim"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="perigo"
            carregando={sair.isPending}
            onClick={() =>
              sair.mutate(
                { chamadoId, recado: recado.trim() || undefined },
                {
                  onSuccess: () => {
                    setRecado('')
                    aoFechar()
                  },
                },
              )
            }
          >
            Sair do apoio
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        {erro ? <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso> : null}
        <p className="text-sm text-tinta-600">
          {donoNome
            ? `A conversa volta para ${dono}, no alto da lista, no bloco Saiu do apoio. Ela sai da sua lista, e o cliente não percebe nada.`
            : 'A conversa continua na fila, para quem pegar. Ela sai da sua lista, e o cliente não percebe nada.'}
        </p>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-tinta-800">
            Recado para {donoNome ? dono : 'a equipe'} (opcional)
          </span>
          <textarea
            value={recado}
            onChange={(e) => setRecado(e.target.value)}
            rows={3}
            placeholder="O que foi feito e o que falta"
            className="w-full rounded-md bg-superficie px-3 py-2 text-sm ring-1 ring-tinta-200"
          />
        </label>
      </div>
    </Modal>
  )
}
